import React, { useEffect, useRef, useState } from 'react';

import { toProjectDateOnly, toUtcDeadline } from 'src/utils/date';
import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';

import Modal from 'src/components/common/ui/Modal';
import type { Task, TaskStatus } from '../types/task.type';
// import { loadWbs } from 'src/lib/wbs/wbsStore';
import { formatPriority } from 'src/lib/taskPriority';
import { useWbsOptions } from '../hooks/useWbsOptions';

import type { CreateTaskInput, UpdateTaskPatch } from '../api/tasks.api';

type Props = {
    open: boolean;
    onClose: () => void;
    projectId: string;
    task?: Task | null;
    canEdit?: boolean;
    onSaved?: (saved: Task) => void;
    onDeleted?: (deletedId: string) => void;
    currentUserId?: string;
    userNameMap?: Record<string, string>;

    // ✅ DB 연동용 콜백
    onCreate: (input: CreateTaskInput) => Promise<Task>;
    onUpdate: (taskId: string, patch: UpdateTaskPatch) => Promise<Task>;
    onDelete: (taskId: string) => Promise<any>;

    fixedWbsNodeId?: string;
    fixedMilestoneId?: string;
};

const STATUSES: TaskStatus[] = ['NOT_STARTED', 'IN_PROGRESS', 'BLOCKED', 'DONE'];

export default function TaskEditorModal({
    open,
    onClose,
    projectId,
    task,
    canEdit,
    onSaved,
    onDeleted,
    currentUserId,
    userNameMap,
    onCreate,
    onUpdate,
    onDelete,
    fixedWbsNodeId,
    fixedMilestoneId,
}: Props) {
    const { setting } = useProjectSettingCtx();
    const zone = setting?.timeZone ?? 'Asia/Seoul';

    const isEdit = !!task;
    const focusRef = useRef<HTMLInputElement>(null);

    // const [wbsOptions, setWbsOptions] = useState<{ id: string; name: string }[]>([]);
    // useEffect(() => {
    //     if (!open) return;
    //     const rows = loadWbs(projectId) || [];
    //     setWbsOptions(rows.map((r: any) => ({ id: r.id, name: r.name })));
    // }, [open, projectId]);

    const needWbsSelect = open && !fixedWbsNodeId;
    const { options: wbsOptions, isLoading: wbsLoading } = useWbsOptions(projectId, needWbsSelect);

    const [wbsNodeId, setWbsNodeId] = useState<string | null>(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState<string>('');
    const [status, setStatus] = useState<TaskStatus>('NOT_STARTED');
    const [urgency, setUrgency] = useState<1 | 2 | 3 | 4 | 5>(3);
    const [importance, setImportance] = useState<1 | 2 | 3 | 4 | 5>(3);
    const [progressNote, setProgressNote] = useState<string>('');
    const [assigneeUserId, setAssigneeUserId] = useState<string | null>(null);
    const [dueDate, setDueDate] = useState<string>('');
    const [isActive, setIsActive] = useState(true);

    useEffect(() => {
        if (!open) return;

        if (task) {
            setWbsNodeId(fixedWbsNodeId ?? task.wbsNodeId ?? null);
            setTitle(task.title || '');
            setDescription(task.description || '');
            setStatus(task.status);
            setUrgency(task.urgency);
            setImportance(task.importance);
            setProgressNote(task.progressNote || '');
            setAssigneeUserId(task.assigneeUserId ?? null);
            setDueDate(task.dueDate ? toProjectDateOnly(task.dueDate, zone) : '');
            setIsActive(!!task.isActive);
        } else {
            setWbsNodeId(fixedWbsNodeId ?? null);
            setTitle('');
            setDescription('');
            setStatus('NOT_STARTED');
            setUrgency(3);
            setImportance(3);
            setProgressNote('');
            setAssigneeUserId(currentUserId ?? null); // UI 기본값(서버도 등록자=담당자)
            setDueDate('');
            setIsActive(true);
        }
    }, [open, task, currentUserId, fixedWbsNodeId, zone]);

    const canSave = canEdit && title.trim().length > 0;

    const handleSave = async () => {
        if (!canSave) return;

        try {
            // const resolvedWbsNodeId = fixedWbsNodeId ?? wbsNodeId ?? task?.wbsNodeId ?? null;
            const resolvedWbsNodeId = fixedWbsNodeId ?? wbsNodeId; // null이면 null 그대로 저장(해제)
            const resolvedMilestoneId = fixedMilestoneId ?? task?.milestoneId ?? null;

            if (isEdit && task) {
                const patch: UpdateTaskPatch = {
                    title: title.trim(),
                    description: description.trim() || null,
                    progressNote: progressNote.trim() || null,
                    status,
                    urgency,
                    importance,
                    assigneeUserId,
                    // dueDate: dueDate || null,
                    dueDate: dueDate || null,
                    isActive,
                    wbsNodeId: resolvedWbsNodeId,
                    milestoneId: resolvedMilestoneId,
                };
                const saved = await onUpdate(task.id, patch);
                onSaved?.(saved);
                onClose();
                return;
            }

            const input: CreateTaskInput = {
                title: title.trim(),
                description: description.trim() || null,
                progressNote: progressNote.trim() || null,
                status,
                urgency,
                importance,
                assigneeUserId,
                // dueDate: dueDate || null,
                dueDate: dueDate || null,
                isActive,
                wbsNodeId: resolvedWbsNodeId,
                milestoneId: resolvedMilestoneId,
            };

            const saved = await onCreate(input);
            onSaved?.(saved);
            onClose();
        } catch (err: any) {
            // const status = err?.response?.status;
            // if (status === 403) return;
            throw err;
        }
    };

    const handleDelete = async () => {
        if (!isEdit || !task || !canEdit) return;
        // eslint-disable-next-line no-restricted-globals
        if (!confirm('정말 삭제하시겠습니까?')) return;
        try {
            await onDelete(task.id);
            onDeleted?.(task.id);
            onClose();
        } catch (err: any) {
            // const status = err?.response?.status;
            // if (status === 403) return;
            throw err;
        }
    };

    const pr = formatPriority({
        scoreStored: task?.priorityScore ?? undefined,
        quadStored: task?.priorityQuadrant ?? undefined,
        urgency,
        importance,
    });

    // ✅ Assignee 선택 옵션(userNameMap 기반, 추측 없음)
    const assigneeOptions = React.useMemo(() => {
        const map = userNameMap ?? {};
        return Object.entries(map)
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [userNameMap]);

    const canUseSelect = assigneeOptions.length > 0;

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={isEdit ? `Task 상세` : '새 Task 생성'}
            size="5xl"
            initialFocusRef={focusRef}
            closeOnBackdrop={false}
        >
            <div className="text-sm space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs text-gray-600 mb-1">WBS</label>
                        <select
                            className="w-full rounded border px-2 py-1"
                            value={fixedWbsNodeId ?? wbsNodeId ?? ''}
                            disabled={!canEdit || !!fixedWbsNodeId}
                            onChange={(e) => setWbsNodeId(e.target.value || null)}
                        >
                            {/* <option value="">— Select WBS —</option> */}
                            <option value="">(미연결)</option>
                            {wbsOptions.map((o) => (
                                <option key={o.id} value={o.id}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                        {wbsLoading && <div className="mt-1 text-[11px] text-gray-400">Loading WBS...</div>}
                    </div>

                    <div>
                        <label className="block text-xs text-gray-600 mb-1">Status</label>
                        <select
                            className="w-full rounded border px-2 py-1"
                            value={status}
                            disabled={!canEdit}
                            onChange={(e) => setStatus(e.target.value as TaskStatus)}
                        >
                            {STATUSES.map((s) => (
                                <option key={s} value={s}>
                                    {s}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-xs text-gray-600 mb-1">Title *</label>
                        <input
                            ref={focusRef}
                            className="w-full rounded border px-2 py-1"
                            value={title}
                            disabled={!canEdit}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-xs text-gray-600 mb-1">Description</label>
                        <textarea
                            className="w-full rounded border px-2 py-1 min-h-[70px]"
                            value={description}
                            disabled={!canEdit}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-xs text-gray-600 mb-1">Progress Notes</label>
                        <textarea
                            className="w-full rounded border px-2 py-1 min-h-[70px]"
                            value={progressNote}
                            disabled={!canEdit}
                            onChange={(e) => setProgressNote(e.target.value)}
                        />
                    </div>

                    <div>
                        <label className="block text-xs text-gray-600 mb-1">Urgency (1~5)</label>
                        <input
                            type="number"
                            min={1}
                            max={5}
                            className="w-full rounded border px-2 py-1"
                            value={urgency}
                            disabled={!canEdit}
                            onChange={(e) => setUrgency(Number(e.target.value) as any)}
                        />
                    </div>

                    <div>
                        <label className="block text-xs text-gray-600 mb-1">Importance (1~5)</label>
                        <input
                            type="number"
                            min={1}
                            max={5}
                            className="w-full rounded border px-2 py-1"
                            value={importance}
                            disabled={!canEdit}
                            onChange={(e) => setImportance(Number(e.target.value) as any)}
                        />
                    </div>

                    <div>
                        <label className="block text-xs text-gray-600 mb-1">Priority</label>
                        <div className={`inline-flex items-center rounded px-2 py-1 text-xs ${pr.className}`}>
                            {pr.text}
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs text-gray-600 mb-1">Due Date</label>
                        <input
                            type="date"
                            className="w-full rounded border px-2 py-1"
                            value={dueDate || ''}
                            disabled={!canEdit}
                            onChange={(e) => setDueDate(e.target.value)}
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-xs text-gray-600 mb-1">Assignee</label>

                        {/* ✅ 멤버 목록(userNameMap)이 있으면 Select 제공 */}
                        {canUseSelect ? (
                            <>
                                <select
                                    className="w-full rounded border px-2 py-1"
                                    value={assigneeUserId ?? ''}
                                    disabled={!canEdit}
                                    onChange={(e) => setAssigneeUserId(e.target.value || null)}
                                >
                                    <option value="">— Unassigned —</option>
                                    {assigneeOptions.map((o) => (
                                        <option key={o.id} value={o.id}>
                                            {o.name} ({o.id})
                                        </option>
                                    ))}
                                </select>

                                {assigneeUserId && userNameMap?.[assigneeUserId] && (
                                    <div className="mt-1 text-[11px] text-gray-500">{userNameMap[assigneeUserId]}</div>
                                )}
                            </>
                        ) : (
                            <>
                                {/* ✅ userNameMap이 없으면 기존 입력 UI 유지(추측 없이 fallback) */}
                                <input
                                    className="w-full rounded border px-2 py-1"
                                    placeholder="assignee userId"
                                    value={assigneeUserId ?? ''}
                                    disabled={!canEdit}
                                    onChange={(e) => setAssigneeUserId(e.target.value || null)}
                                />
                                {assigneeUserId && userNameMap?.[assigneeUserId] && (
                                    <div className="mt-1 text-[11px] text-gray-500">{userNameMap[assigneeUserId]}</div>
                                )}
                            </>
                        )}
                    </div>

                    <label className="inline-flex items-center gap-2 text-xs">
                        <input
                            type="checkbox"
                            className="accent-gray-700"
                            checked={isActive}
                            disabled={!canEdit}
                            onChange={(e) => setIsActive(e.target.checked)}
                        />
                        Active
                    </label>
                </div>

                <div className="flex justify-between pt-2">
                    <div>
                        {isEdit && (
                            <button
                                type="button"
                                className="text-xs rounded border px-2.5 py-1 hover:bg-gray-50 text-rose-600 disabled:opacity-50"
                                onClick={handleDelete}
                                disabled={!canEdit}
                            >
                                Delete
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            className="text-xs rounded border px-2.5 py-1 hover:bg-gray-50"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="text-xs rounded px-2.5 py-1 text-white bg-gray-900 hover:bg-gray-700 disabled:opacity-50"
                            onClick={handleSave}
                            disabled={!canSave}
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
